import { ArrowUpOnSquareIcon, TrashIcon } from '@heroicons/react/24/solid';
import React, { useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Banner, { BannerVariant } from 'components/Banner/Banner';
import Button, { ButtonVariant } from 'components/Button/Button';
import CTAModal from 'components/CTAModal/CTAModal';
import Icon, { IconVariant } from 'components/Icon/Icon';
import ImageUploadButton from 'components/ImageUploader/ImageUploadButton';
import { t } from 'locales/i18n';

export enum MediaType {
  VIDEO = 'video',
  DOCUMENT = 'document',
}

interface IMediaUploaderProps {
  acceptFileType: string;
  buttonText?: string;
  deleteConfirmation?: string;
  deleteDescription?: string;
  deleteTitle?: string;
  description?: string;
  emptyDescription?: string;
  fileSizeLimit?: number;
  isLoading: boolean;
  mediaType: MediaType;
  mediaUrl?: string;
  onDelete?: () => Promise<void>;
  onUpload: (asset: Blob) => void;
}
const MediaUploader = ({
  acceptFileType,
  buttonText = t('Upload Video'),
  deleteConfirmation = t('Are you sure you want to delete this file?'),
  deleteDescription = t("Deleting this file is permanent and can't be undone."),
  deleteTitle = t('Delete File'),
  description = '',
  emptyDescription = t('No video added yet.'),
  fileSizeLimit,
  isLoading,
  mediaType,
  mediaUrl,
  onDelete,
  onUpload,
}: IMediaUploaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const inputFileRef = useRef<HTMLInputElement>(null);

  const defaultClassName = twMerge('flex flex-col justify-center p-10');

  const containerClassName = twMerge(
    'bg-kira-slate-50',
    'border-kira-slate-200',
    'border',
    'relative',
    'rounded-lg',
    'w-full'
  );

  const handleDelete = () => {
    if (onDelete) onDelete();
    setIsOpen(false);
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const fileSizeKiloBytes = event.target.files[0].size / (1024 * 1024);
      if (fileSizeLimit && fileSizeKiloBytes > fileSizeLimit) {
        setErrorMessage(
          t(
            'File exceeds the upload limit. Please compress your file or try again with a different file.'
          )
        );
        return;
      }
    }

    if (event.target?.files && event.target.files[0] instanceof Blob) {
      setErrorMessage('');
      onUpload(event.target.files[0]);
    }
  };

  return (
    <>
      {errorMessage && (
        <Banner
          message={errorMessage}
          variant={BannerVariant.ERROR}
          bannerDataTestId="fileError"
        />
      )}
      <div className={containerClassName}>
        {mediaUrl ? (
          <div className="relative">
            <ImageUploadButton
              icon={<TrashIcon />}
              onClick={() => setIsOpen(true)}
              dataTestId="delete-video"
            />
            <video
              className="mx-auto w-full max-w-[1500px] rounded-lg"
              data-testid="videoElement"
              controls
            >
              <source src={mediaUrl} type="video/mp4" />
            </video>
            <CTAModal
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              title={deleteTitle}
              description={deleteDescription}
              confirmation={deleteConfirmation}
              action={handleDelete}
              buttonDataTestId="delete"
            />
          </div>
        ) : (
          <div className={defaultClassName}>
            <Icon icon={<ArrowUpOnSquareIcon />} variant={IconVariant.LARGE} />
            <div className="prose-title-h4 mt-4 text-center">
              {emptyDescription}
              <div className="prose-body-sm-reg mt-2">{description}</div>
            </div>
            <div className="mt-4 flex items-center justify-center self-center">
              <Button
                dataTestId="uploadVideoButton"
                variant={ButtonVariant.SECONDARY}
                type="button"
                isLoading={isLoading}
                disabled={isLoading}
                onClick={() => inputFileRef?.current?.click()}
                buttonText={buttonText}
              />
              <input
                type="file"
                ref={inputFileRef}
                id={`file-uploader-${mediaType}`}
                data-testid="fileUpload"
                accept={acceptFileType}
                name="file-uploader"
                className="hidden"
                onChange={handleUpload}
                disabled={isLoading}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MediaUploader;
